import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Package,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { equipmentCalendarAPI, equipmentAPI } from '../services/api';

const EquipmentCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [dailySummary, setDailySummary] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEquipment, setSelectedEquipment] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const categories = useMemo(() => {
    const cats = [...new Set(equipmentList.map(eq => eq.category))];
    return cats.sort();
  }, [equipmentList]);

  const filteredEquipment = useMemo(() => {
    if (selectedCategory === 'all') return equipmentList;
    return equipmentList.filter(eq => eq.category === selectedCategory);
  }, [equipmentList, selectedCategory]);

  const dateRange = useMemo(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    
    if (viewMode === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    } else {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      end.setDate(start.getDate() + 6);
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, [currentDate, viewMode]);

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedCategory, selectedEquipment]);

  useEffect(() => {
    fetchEquipmentList();
  }, []);

  const fetchEquipmentList = async () => {
    try {
      const res = await equipmentAPI.getAll();
      setEquipmentList(res.data);
    } catch (error) {
      console.error('Fout bij ophalen apparatuur:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await equipmentCalendarAPI.getDailySummary(
        dateRange.start,
        dateRange.end,
        selectedEquipment !== 'all' ? selectedEquipment : undefined,
        selectedCategory !== 'all' ? selectedCategory : undefined
      );
      setDailySummary(res.data.daily_summary || []);
    } catch (error) {
      console.error('Fout bij ophalen kalendergegevens:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  const calendarDays = useMemo(() => {
    const days = [];
    const start = new Date(currentDate);
    start.setDate(1);
    
    const firstDay = start.getDay();
    
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      days.push(date.toISOString().split('T')[0]);
    }
    
    return days;
  }, [currentDate]);

  const getDaySummary = (dateStr) => {
    return dailySummary.find(d => d.date === dateStr);
  };

  const getDayUtilization = (dateStr) => {
    const summary = getDaySummary(dateStr);
    if (!summary) return { total: 0, booked: 0, percentage: 0 };
    
    let totalQty = 0;
    let bookedQty = 0;
    
    Object.values(summary.equipment).forEach(eq => {
      totalQty += eq.total_quantity;
      bookedQty += eq.booked_quantity;
    });
    
    return {
      total: totalQty,
      booked: bookedQty,
      percentage: totalQty > 0 ? Math.round((bookedQty / totalQty) * 100) : 0
    };
  };

  const getUtilizationColor = (percentage) => {
    if (percentage === 0) return 'bg-gray-50 border-gray-200';
    if (percentage < 30) return 'bg-green-50 border-green-200';
    if (percentage < 60) return 'bg-yellow-50 border-yellow-200';
    if (percentage < 90) return 'bg-orange-50 border-orange-200';
    return 'bg-red-50 border-red-200';
  };

  const monthNames = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

  const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];

  const isToday = (dateStr) => {
    return dateStr === new Date().toISOString().split('T')[0];
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Apparatuur Beschikbaarheid</h1>
          <p className="text-gray-500 mt-1">Visueel overzicht van apparatuur bezetting en beschikbaarheid</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold min-w-[200px] text-center">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="outline" onClick={goToToday}>
                Vandaag
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setSelectedEquipment('all'); }}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="Alle Categorieën" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Categorieën</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedEquipment} onValueChange={setSelectedEquipment}>
                <SelectTrigger className="w-[200px]" data-testid="equipment-filter">
                  <SelectValue placeholder="Alle Apparatuur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Apparatuur</SelectItem>
                  {filteredEquipment.map(eq => (
                    <SelectItem key={eq.id} value={eq.id}>{eq.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-6 text-sm">
        <span className="font-medium">Bezetting:</span>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-gray-50 border-gray-200 rounded border"></div>
          <span>0%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-50 border-green-200 rounded border"></div>
          <span>1-30%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-yellow-50 border-yellow-200 rounded border"></div>
          <span>30-60%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-orange-50 border-orange-200 rounded border"></div>
          <span>60-90%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-50 border-red-200 rounded border"></div>
          <span>90-100%</span>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="text-center font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
              
              {calendarDays.map((dateStr, index) => {
                if (!dateStr) {
                  return <div key={`empty-${index}`} className="h-24 bg-gray-50 rounded"></div>;
                }
                
                const utilization = getDayUtilization(dateStr);
                const dayNum = new Date(dateStr).getDate();
                const isSelected = selectedDay === dateStr;
                
                return (
                  <div
                    key={dateStr}
                    className={`h-24 rounded border cursor-pointer transition-all hover:shadow-md ${
                      getUtilizationColor(utilization.percentage)
                    } ${isToday(dateStr) ? 'ring-2 ring-blue-500' : ''} ${
                      isSelected ? 'ring-2 ring-purple-500 shadow-lg' : ''
                    }`}
                    onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                    data-testid={`calendar-day-${dateStr}`}
                  >
                    <div className="p-2 h-full flex flex-col">
                      <div className="flex justify-between items-start">
                        <span className={`text-sm font-medium ${isToday(dateStr) ? 'text-blue-600' : ''}`}>
                          {dayNum}
                        </span>
                        {utilization.booked > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {utilization.percentage}%
                          </Badge>
                        )}
                      </div>
                      {utilization.total > 0 && (
                        <div className="mt-auto">
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <Package className="w-3 h-3" />
                            <span>{utilization.booked}/{utilization.total}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedDay && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
              {new Date(selectedDay).toLocaleDateString('nl-NL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const summary = getDaySummary(selectedDay);
              if (!summary) return <p className="text-gray-500">Geen gegevens beschikbaar</p>;
              
              const equipmentEntries = Object.entries(summary.equipment);
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {equipmentEntries.map(([eqId, eqData]) => {
                    const available = eqData.total_quantity - eqData.booked_quantity;
                    const utilizationPct = eqData.total_quantity > 0 
                      ? Math.round((eqData.booked_quantity / eqData.total_quantity) * 100) 
                      : 0;
                    
                    return (
                      <div
                        key={eqId}
                        className={`p-4 rounded-lg border ${
                          available === 0 ? 'bg-red-50 border-red-200' :
                          available <= 2 ? 'bg-yellow-50 border-yellow-200' :
                          'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-gray-900">{eqData.name}</h4>
                            <p className="text-xs text-gray-500">{eqData.category}</p>
                          </div>
                          {available === 0 ? (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Beschikbaar:</span>
                            <span className={`font-semibold ${
                              available === 0 ? 'text-red-600' :
                              available <= 2 ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {available} / {eqData.total_quantity}
                            </span>
                          </div>
                          
                          <Progress value={utilizationPct} className="h-2" />
                          
                          {eqData.bookings && eqData.bookings.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs font-medium text-gray-700 mb-2">Boekingen:</p>
                              {eqData.bookings.map((booking, idx) => (
                                <div key={idx} className="text-xs text-gray-600 mb-1">
                                  • {booking.project_name} ({booking.quantity}x)
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EquipmentCalendar;
